import { ChevronLeft, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { BALL_IN_COURT_LABELS, EVENT_LOG_STORY_MAX, eventEntityLabel, eventSentence, turnsOf, type EventLogRow, type EventLogStory as Story, type TurnSpan } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SkeletonLines } from '../../../../components/Skeleton';
import { EmptyState, SystemState } from '../../../../components/system-states/SystemState';
import { errorMessage, type ApiError } from '../../../../lib/api-client';
import { formatDate, formatDateTime } from '../../../../lib/format';
import { EventLogDetail } from './EventLogDetail';
import { actorLabelOf, durationText, nowWithText, shortId } from './event-log-words';
import styles from './EventLogStory.module.css';

// The drill-down from Event log A — "One record's story": a record's life from top to bottom, oldest first, with a
// strip of whose turn it was and for how long, worked out from the ball-in-court each event carries (the same field
// the time-in-stage reports split by). Not drawn: "How" (no event records the screen used), the named person whose
// turn it is (no event records one), "Why" and "Also show" (no cause or link between records is stored).

const MINUTE_MS = 60_000;

interface EventLogStoryProps {
  readonly entityType: string;
  readonly entityId: string;
  readonly story: Story | undefined;
  readonly error: ApiError | null;
  readonly isPending: boolean;
  readonly onRetry: () => void;
  readonly selectedId: string | undefined;
  readonly onSelect: (row: EventLogRow) => void;
  readonly onAllEvents: () => void;
  /** Tablet and phone: the timeline alone; the clicked event opens as its own view. */
  readonly isSingleColumn: boolean;
  readonly onCloseEvent: () => void;
  readonly now?: Date;
}

export function EventLogStory({ entityType, entityId, story, error, isPending, onRetry, selectedId, onSelect, onAllEvents, isSingleColumn, onCloseEvent, now = new Date() }: EventLogStoryProps): ReactNode {
  const rows = story?.rows ?? [];
  const name = story?.record?.name ?? `${eventEntityLabel(entityType)} ${shortId(entityId)}`;
  const opened = rows.find((row) => row.id === selectedId) ?? null;
  const selected = opened ?? (isSingleColumn ? null : (rows[rows.length - 1] ?? null));
  const spans = turnsOf(rows, now);
  const lastTurn = [...rows].reverse().find((row) => row.ballInCourt !== null)?.ballInCourt ?? null;

  if (isSingleColumn && opened) {
    return <EventLogDetail row={opened} onBack={onCloseEvent} backLabel={name} />;
  }

  const header = (
    <header className={styles.header}>
      <div className={styles.heading}>
        <p className={styles.crumb}>Event log · one record&apos;s story</p>
        <h1 className={styles.title}>{name}</h1>
        <p className={styles.sub}>
          <span className={styles.tag}>{eventEntityLabel(entityType)}</span>
          <span>{summaryOf(story, rows, spans, lastTurn, now)}</span>
        </p>
      </div>
      <Button variant="outline" icon={ChevronLeft} onClick={onAllEvents}>
        All events
      </Button>
    </header>
  );

  if (isPending && !story) {
    return (
      <div className={styles.story}>
        {header}
        <SkeletonLines count={8} />
      </div>
    );
  }

  if (error && !story) {
    return (
      <div className={styles.story}>
        {header}
        <SystemState
          icon={TriangleAlert}
          tone="danger"
          title="This record's story didn't load"
          body={errorMessage(error)}
          actions={
            <Button variant="quiet" onClick={onRetry}>
              Try again
            </Button>
          }
          isInset
        />
      </div>
    );
  }

  return (
    <div className={styles.story}>
      {header}

      {rows.length === 0 ? (
        <EmptyState title="Nothing recorded for this record" body="Every change to it will appear here, oldest first, as it happens." action={null} isInset />
      ) : (
        <>
          <TurnStrip spans={spans} />

          {story?.isTruncated && <p className={styles.note}>This record has more than {EVENT_LOG_STORY_MAX} events. These are the newest {EVENT_LOG_STORY_MAX}.</p>}

          <div className={styles.layout}>
            <section className={styles.timeline} aria-labelledby="event-story-timeline">
              <h2 id="event-story-timeline" className={styles.sectionTitle}>
                What happened, oldest first
              </h2>
              <ol className={styles.events}>
                {rows.map((row) => (
                  <li key={row.id} className={styles.item}>
                    <span className={styles.dot} data-system={row.actorType === 'system' || undefined} aria-hidden />
                    <button type="button" className={styles.event} aria-current={row.id === selected?.id || undefined} onClick={() => onSelect(row)}>
                      <span className={styles.eventTitle}>
                        {eventSentence(row)} <code className={styles.code}>{row.eventType}</code>
                      </span>
                      <span className={styles.eventMeta}>
                        {formatDateTime(row.occurredAt)} · {actorLabelOf(row)}
                        {row.ballInCourt && row.ballInCourt !== 'none' ? ` · then ${BALL_IN_COURT_LABELS[row.ballInCourt]}'s turn` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>

            {!isSingleColumn && selected && (
              <div className={styles.detailArea}>
                <EventLogDetail row={selected} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** "Senior product designer · Nest Digital · 6 events · 9 days · now with Hiring manager for 0 days". */
function summaryOf(story: Story | undefined, rows: readonly EventLogRow[], spans: readonly TurnSpan[], lastTurn: EventLogRow['ballInCourt'], now: Date): string {
  if (!story) return '';
  const first = rows[0];
  const parts = [story.record?.detail, story.ventureName].filter((part): part is string => typeof part === 'string' && part !== '');
  parts.push(`${rows.length} ${rows.length === 1 ? 'event' : 'events'}`);
  if (first) parts.push(durationText(Math.max(0, Math.round((now.getTime() - Date.parse(first.occurredAt)) / MINUTE_MS))));
  const turn = nowWithText(spans, lastTurn);
  if (turn) parts.push(turn);
  return parts.join(' · ');
}

/** Whose turn it was: one band per stretch, as wide as its share of the time, with its holder and how long. */
function TurnStrip({ spans }: { readonly spans: readonly TurnSpan[] }): ReactNode {
  if (spans.length === 0) {
    return (
      <section className={styles.strip} aria-labelledby="event-story-turns">
        <h2 id="event-story-turns" className={styles.sectionTitle}>
          Whose turn it was
        </h2>
        <p className={styles.note}>No event on this record says whose turn it was.</p>
      </section>
    );
  }
  // A stretch of a few minutes still gets a sliver, so every holder is visible.
  const total = spans.reduce((sum, span) => sum + Math.max(1, span.minutes), 0);
  return (
    <section className={styles.strip} aria-labelledby="event-story-turns">
      <h2 id="event-story-turns" className={styles.sectionTitle}>
        Whose turn it was — the time each side held it
      </h2>
      <ol className={styles.bands}>
        {spans.map((span) => (
          <li
            key={span.from}
            className={styles.band}
            data-holder={span.holder}
            style={{ flexGrow: Math.max(1, span.minutes) / total }}
            title={`${BALL_IN_COURT_LABELS[span.holder]} · ${durationText(span.minutes)} · from ${formatDateTime(span.from)}${span.to ? ` to ${formatDateTime(span.to)}` : ' to now'}`}
          >
            <span className={styles.bandLabel}>{BALL_IN_COURT_LABELS[span.holder]}</span>
            <span className={styles.bandTime}>
              {durationText(span.minutes)} · from {formatDate(span.from)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

